import { ApiFormattedResponse } from "@models/api_formatted_response"
import { query } from "@utils/graphql"
import {
  GetCustomerOwnersData,
  GetCustomerOwnersResponse,
  Preference,
  PreferenceResponse,
  Product,
  ProductsResponse,
  QueryGetCustomerByIdArgs
} from "__generated__/graphql"
import updateAutoFormalizeInvoicesMutation from "__generated__/queries/mutations/updateAutoFormalizeInvoices"
import updateRequireAccountApproval from "__generated__/queries/mutations/updateRequireAccountApproval"
import getCustomerOwners from "__generated__/queries/queries/getCustomerOwners"
import getPreference from "__generated__/queries/queries/getPreference"
import products from "__generated__/queries/queries/products"

// Codegen for admin-panel-v2 is run manually; until it picks up the
// new GraphQL surface, mirror the backend enum locally so callers get
// type-safety on the four allowed values.
export const AUTO_FORMALIZE_INVOICES_VALUES = [
  "no",
  "yes",
  "yesOnlyTr",
  "yesOnlyNotTr"
] as const
export type AutoFormalizeInvoicesPreference =
  (typeof AUTO_FORMALIZE_INVOICES_VALUES)[number]

// Mirrors the backend `CustomerPackageName` / `CustomerProductPeriodType`
// enums, which are not exposed through the GraphQL schema.
export const CUSTOMER_PACKAGE_NAMES = [
  "FreeTrial",
  "Start",
  "Grow",
  "Scale",
  "Special"
] as const
export type CustomerPackageName = (typeof CUSTOMER_PACKAGE_NAMES)[number]

export const CUSTOMER_PERIOD_MODES = ["Monthly", "Yearly", "Once"] as const
export type CustomerPeriodMode = (typeof CUSTOMER_PERIOD_MODES)[number]

export type CustomerListFilters = {
  search?: string
  appStatus?: string
  accountStatus?: string
  isEmailVerified?: boolean
  packageName?: CustomerPackageName
  periodMode?: CustomerPeriodMode
}

export async function getCustomers(
  data: CustomerListFilters & {
    pageNumber: number
    pageSize: number
  }
): Promise<{
  isSuccess: boolean
  data: {
    docs: GetCustomerOwnersData[] | null
    totalDocs: number | undefined
  }
}> {
  const response = await query<{
    getCustomerOwners?: GetCustomerOwnersResponse
  }>({
    query: getCustomerOwners,
    fetchPolicy: "network-only",
    variables: {
      ...(data.search && { search: data.search }),
      ...(data.appStatus && { appStatus: data.appStatus }),
      ...(data.accountStatus && { accountStatus: data.accountStatus }),
      ...(data.isEmailVerified !== undefined && {
        isEmailVerified: data.isEmailVerified
      }),
      ...(data.packageName && { packageName: data.packageName }),
      ...(data.periodMode && { periodMode: data.periodMode }),
      pageNumber: data.pageNumber,
      pageSize: data.pageSize
    }
  })
  if (!response.isSuccess) {
    return {
      ...response,
      data: {
        docs: null,
        totalDocs: 0
      }
    }
  }
  const newPerson = response?.data?.getCustomerOwners?.data.docs.map((p) => ({
    customerPerson: {
      ...p.customerPerson,
      _id: p.customer?._id,
      company: p.customer?.companyName,
      accountStatus: p.customer?.accountStatus,
      appStatus: p.customer?.appStatus,
      createdAt: p.customer?.createdAt,
      mainPlan: p.customer?.package?.name,
      renewalType: p.customer?.package?.periodMode,
      allowConnect: p.customer?.allowConnect,
      owner: `${p.customerPerson?.firstName} ${p.customerPerson?.lastName}`
    },
    customer: {
      ...p.customer,
      _id: p.customer?._id
    }
  }))
  return {
    isSuccess: true,
    data: {
      docs: newPerson as GetCustomerOwnersData[],
      totalDocs: response.data?.getCustomerOwners?.data.totalDocs || 0
    }
  }
}

export async function updateAccountApproval(
  reference: boolean
): Promise<ApiFormattedResponse<PreferenceResponse>> {
  const response = await query<{
    updateRequireAccountApproval?: PreferenceResponse
  }>({
    query: updateRequireAccountApproval,
    variables: { approvalRequired: reference }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: {
      data: response?.data?.updateRequireAccountApproval?.data as Preference
    }
  }
}

export async function getPreferences(): Promise<
  ApiFormattedResponse<PreferenceResponse>
> {
  const response = await query<{
    getPreference?: PreferenceResponse
  }>({
    query: getPreference
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: { data: response?.data?.getPreference?.data as Preference }
  }
}

export async function updateAutoFormalizeInvoices(
  value: AutoFormalizeInvoicesPreference
): Promise<ApiFormattedResponse<PreferenceResponse>> {
  const response = await query<{
    updateAutoFormalizeInvoices?: PreferenceResponse
  }>({
    query: updateAutoFormalizeInvoicesMutation,
    variables: { value }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: {
      data: response?.data?.updateAutoFormalizeInvoices?.data as Preference
    }
  }
}

export async function getProducts(
  data: QueryGetCustomerByIdArgs
): Promise<ApiFormattedResponse<Product[]>> {
  const response = await query<
    { products: ProductsResponse },
    QueryGetCustomerByIdArgs
  >({
    query: products,
    variables: data
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { ...response, data: response.data?.products?.data }
}
